import { inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { SnackbarService, ToastType } from '@siat-fe/shared';
import {
  catchError,
  EMPTY,
  filter,
  from,
  fromEvent,
  Observable,
  of,
  switchMap,
  tap,
  timer,
} from 'rxjs';

/**
 * Service responsible for checking, downloading, and activating PWA updates.
 * Fully reactive implementation using RxJS flows without async/await.
 */
@Injectable({ providedIn: 'root' })
export class UpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly snackbarService = inject(SnackbarService);
  private readonly POLLING_INTERVAL = 60 * 60 * 1000;

  /**
   * Minimum time between two update checks triggered by a tab regaining visibility.
   */
  private readonly MIN_VISIBILITY_CHECK_INTERVAL = 5 * 60 * 1000;

  /**
   * Timestamp of the last update check, used to throttle visibility-triggered checks.
   */
  private lastCheckedAt = 0;

  constructor() {
    if (!this.swUpdate.isEnabled) return;

    this.initUpdateListener();
    this.initPolling();
    this.initVisibilityCheck();
    this.initUnrecoverableListener();
  }

  /**
   * This method is called when a new version is available.
   */
  private initUpdateListener(): void {
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        tap(() => this.showUpdateNotification()),
      )
      .subscribe();
  }

  /**
   * It checks for updates every hour.
   */
  private initPolling(): void {
    timer(10000, this.POLLING_INTERVAL)
      .pipe(switchMap(() => this.runThrottledCheck()))
      .subscribe();
  }

  /**
   * Checks for an update when the tab regains visibility, throttled to avoid
   * hammering the server when the user switches tabs frequently.
   */
  private initVisibilityCheck(): void {
    fromEvent(document, 'visibilitychange')
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        filter(
          () =>
            Date.now() - this.lastCheckedAt >=
            this.MIN_VISIBILITY_CHECK_INTERVAL,
        ),
        switchMap(() => this.runThrottledCheck()),
      )
      .subscribe();
  }

  /**
   * Reacts to an unrecoverable service worker state by notifying the user and reloading.
   */
  private initUnrecoverableListener(): void {
    this.swUpdate.unrecoverable
      .pipe(
        tap(() =>
          this.snackbarService.showDangerSnackbar(
            "Errore critico dell'applicazione. Ricaricamento in corso...",
          ),
        ),
        switchMap(() => timer(3000)),
        tap(() => window.location.reload()),
      )
      .subscribe();
  }

  /**
   * Calls checkForUpdate and records the check time, for the polling and visibility triggers.
   * @returns {Observable<boolean>} whether a new version was found.
   */
  private runThrottledCheck(): Observable<boolean> {
    this.lastCheckedAt = Date.now();

    return from(this.swUpdate.checkForUpdate()).pipe(
      catchError(() => of(false)),
    );
  }

  /**
   * Check for updates manually.
   */
  public checkForUpdateManual(): void {
    if (!this.swUpdate.isEnabled) {
      this.snackbarService.showInfoSnackbar('SW disabilitato in dev mode.');
      return;
    }

    from(this.swUpdate.checkForUpdate())
      .pipe(
        tap((found) => {
          if (!found) {
            this.snackbarService.showInfoSnackbar(
              "L'applicazione è aggiornata all'ultima versione.",
            );
          }
        }),
        catchError((_err) => {
          this.snackbarService.showDangerSnackbar(
            'Errore durante il controllo aggiornamenti.',
          );
          return EMPTY;
        }),
      )
      .subscribe();
  }

  /**
   * Activate update and reload.
   */
  private activateUpdateAndReload(): void {
    from(this.swUpdate.activateUpdate())
      .pipe(
        switchMap(() => this.clearCache()),
        tap(() => window.location.reload()),
        catchError(() => {
          window.location.reload();
          return EMPTY;
        }),
      )
      .subscribe();
  }

  /**
   * Show update notification.
   */
  private showUpdateNotification(): void {
    this.snackbarService.showCustomActionSnackbar(
      "È disponibile una nuova versione dell'applicazione.",
      'Clicca per ricaricare',
      () => this.activateUpdateAndReload(),
      ToastType.INFO,
      0,
    );
  }

  /**
   * Clear cache.
   */
  private clearCache() {
    if (!('caches' in window)) return of(null);

    return from(caches.keys()).pipe(
      switchMap((keys) => Promise.all(keys.map((key) => caches.delete(key)))),
      catchError(() => of(null)),
    );
  }
}
